import { Capacitor } from '@capacitor/core';
import {
  IonButton, IonButtons, IonCheckbox, IonLabel, IonText,
} from '@ionic/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ENVIRONMENT from '@/environments/environment';
import { AnalyticsOrigination, AnalyticsStepNumber, ShippingContinueMode } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { NotificationType } from '@/interfaces/CustomerInterface/CustomerInterface';
import { EDNIModalRoutes } from '@/interfaces/EDniModalInterface/EDniModalInterface';
import { sendNotification } from '@/redux/eDniModal/eDniModalAPI';
import { getTimeResult, registerShippingContinue } from '@/services/analytics';
import { requestVersionControl } from '@/services/api/version';
import { saveVersionControl } from '@/redux/configOptions/config';
import { RootState } from '@/store';

import styles from './OperationShippingForm.module.scss';
import { VersionControl } from '@/interfaces/VersionControlInterface/VersionControlInterface';

interface OperationShippingFormProps {
  nextStep: (route: EDNIModalRoutes) => void;
  handleLoading: (e: boolean) => void;
  sendIFrameUrl: (url: string) => void;
}

export default function OperationShippingForm({
  handleLoading, sendIFrameUrl, nextStep,
}: OperationShippingFormProps) {
  const [optionSelected, setOptionSelected] = useState<NotificationType | null>(null);
  const creditProposal = useSelector((state: RootState) => state.creditProposal.data);
  const [versionControl, setVersionControl] = useState<VersionControl>();
  const [initTime] = useState<dayjs.Dayjs>(dayjs());
  const checkIsVersionControl = useSelector(
    (state: RootState) => state.configSlice.versionControl,
  );
  const dispatch = useDispatch();

  const handleContinue = async () => {
    try {
      if (creditProposal) {
        const shippingAnalytics = {
          time_elapsed: getTimeResult(initTime),
          step_number: AnalyticsStepNumber.Shipping,
          continue_mode: optionSelected === NotificationType.EMAIL ?
          ShippingContinueMode.EMAIL : optionSelected === NotificationType.SMS
          ? ShippingContinueMode.SMS : ShippingContinueMode.SAMEDEVICE
        }
        registerShippingContinue({ origin: AnalyticsOrigination.App, uuid: creditProposal.credit_proposal_id }, shippingAnalytics);
        if (optionSelected === NotificationType.CONTINUE) {
          // if (customerClassification?.require_enrichment_personal_finances) {
          const leadToken = sessionStorage.getItem('leadToken');
          const continuationUrl = `${ENVIRONMENT.URL_PEPPERONE}/?lead_token=${leadToken}&credit_proposal_id=${creditProposal.credit_proposal_id}&super_express=true&is_app=true`;
          if (versionControl?.super_express_continuation) {
            nextStep(EDNIModalRoutes.TPV);
          } else {
            handleLoading(true);
            sendIFrameUrl(continuationUrl);
          }
          // } else {
          //   nextStep(EDNIModalRoutes.TPV);
          // }
        } else {
          const notification = {
            notify_type: optionSelected,
          };
          await sendNotification(creditProposal.credit_proposal_id, notification);
          // handleClose()
          nextStep(EDNIModalRoutes.OPERATION_SHIPPING_FINISHED);
        }
      } else {
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
        handleLoading(false);
      }
    } catch (error) {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  const shallShowBtn = () => {
    if (creditProposal?.product !== 'PLS') {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform() && !versionControl) {
      // if (!versionControl) {
      if (!checkIsVersionControl) {
        requestVersionControl().then((res) => {
          const versionRes = res as VersionControl;
          setVersionControl(versionRes);
          dispatch(saveVersionControl(versionRes));
        });
      } else {
        setVersionControl(checkIsVersionControl);
      }
    }
  }, [versionControl]);

  useEffect(() => {
    handleLoading(false);
  }, []);

  return (
    <div className={styles.operationShippingWrapper}>
      <IonLabel className={styles.modalTitle}>Elige dónde quieres que enviemos el enlace al cliente:</IonLabel>
      <div className={styles.operationShippingList}>
        <div className={styles.operationShippingOption} onClick={() => setOptionSelected(NotificationType.EMAIL)}>
          <IonCheckbox className={classNames(optionSelected === NotificationType.EMAIL ? styles.operationShippingCheckboxChecked : styles.operationShippingCheckbox)} checked={optionSelected === NotificationType.EMAIL} labelPlacement="end">
            <div className={styles.operationShippingText}>
              <IonText className={styles.operationShippingOptionName}>Email</IonText>
              <IonText className={styles.operationShippingCustomerData}>{creditProposal?.customer?.email}</IonText>
            </div>
          </IonCheckbox>
        </div>
        <div className={styles.operationShippingOption} onClick={() => setOptionSelected(NotificationType.SMS)}>
          <IonCheckbox className={classNames(optionSelected === NotificationType.SMS ? styles.operationShippingCheckboxChecked : styles.operationShippingCheckbox)} checked={optionSelected === NotificationType.SMS} labelPlacement="end">
            <div className={styles.operationShippingText}>
              <IonText className={styles.operationShippingOptionName}>Sms</IonText>
              <IonText className={styles.operationShippingCustomerData}>{creditProposal?.customer?.phones[0]}</IonText>
            </div>
          </IonCheckbox>
        </div>
        {shallShowBtn() && (
          <div className={styles.operationShippingOption} onClick={() => setOptionSelected(NotificationType.CONTINUE)}>
            <IonCheckbox className={classNames(optionSelected === NotificationType.CONTINUE ? styles.operationShippingCheckboxChecked : styles.operationShippingCheckbox)} checked={optionSelected === NotificationType.CONTINUE} labelPlacement="end">
              <div className={styles.operationShippingText}>
                <IonText className={styles.operationShippingOptionName}>Continuar en este dispositivo</IonText>
              </div>
            </IonCheckbox>
          </div>
        )}
      </div>
      <IonButtons className={styles.nextBtnWrapper}>
        <IonButton className="nextButton" onClick={handleContinue} disabled={!optionSelected}>Continuar</IonButton>
      </IonButtons>
    </div>
  );
}
